import { AxiosResponse } from 'axios';
import _ from 'lodash';
import { apiErrorHandling } from 'src/api/apierrorhandling';
import { Asset, Payout } from 'src/models';
import { PayoutStatus } from 'src/models/enum';
import { faker } from '@faker-js/faker';

export const getHumanReadableSize = (size: number, decimals = 2): string => {
  if (size === 0) {
    return '';
  }
  const k = 1024;
  const sizeFormats = ['Bytes', 'KB', 'MB', 'GB'];
  let i = Math.floor(Math.log(size) / Math.log(k));
  i = i < sizeFormats.length ? i : sizeFormats.length - 1;
  return `${parseFloat((size / Math.pow(k, i)).toFixed(decimals))} ${sizeFormats[i]}`;
};

export const stringLocale = 'en-US';

export const formatNumber = (input: number | string) => {
  if (typeof input === 'string') {
    return parseFloat(input).toLocaleString(stringLocale, {
      minimumFractionDigits: 2,
    });
  }
  if (typeof input === 'number') {
    return input.toLocaleString(stringLocale, { minimumFractionDigits: 2 });
  }
  return '';
};

export const awaitUtil = async <T>(asyncFunction: Promise<AxiosResponse<T>>) => {
  try {
    const data = await asyncFunction;
    return [data, null] as const;
  } catch (error: any) {
    apiErrorHandling(error);
    return [null, error] as const;
  }
};

export const calcRemainingRentFromLastPayment = (payouts: Payout[], string: boolean): number | string => {
  const filterdPayouts = payouts.filter((p) => p.status === PayoutStatus.succeeded || p.status === PayoutStatus.transferred);
  if (string) {
    const rentFromLastPayment = filterdPayouts[0]?.remainingRent?.$numberDecimal.toString() ?? '0.00';
    return rentFromLastPayment;
  } else {
    const rentFromLastPayment = filterdPayouts[0]?.remainingRent?.$numberDecimal.toString() ?? 0;
    return Number(rentFromLastPayment);
  }
};

export const isNullUndefinedOrEmpty = (value: any) => {
  return value === null || value === undefined || value === '';
};

export const getFormikValueWithSubFields = (values: any, fieldList: string[]) => {
  let value = values;
  try {
    fieldList.forEach((field) => {
      value = _.get(value, field);
    });
  } catch (e) {
    return null;
  }

  return value;
};

export const asyncForEach = async (array: any, callback: any) => {
  for (let index = 0; index < array.length; index++) {
    await callback(array[index], index, array);
  }
};

export const getCoordinatesFromAddress = async (address: string): Promise<google.maps.LatLng> => {
  const geocoder = new google.maps.Geocoder();
  return new Promise((resolve, reject) => {
    geocoder.geocode({ address }, (results, status) => {
      if (status === 'OK') {
        resolve(results[0].geometry.location);
      } else {
        reject(status);
      }
    });
  });
};

export const convertToInt = (value: string) => {
  const number = parseInt(value);
  if (isNaN(number)) {
    return 0;
  } else {
    return number;
  }
};

export const convertToFloat = (value: string) => {
  const number = parseFloat(value);
  if (isNaN(number)) {
    return 0;
  } else {
    return number;
  }
};

export const joinAddress = (address: string, postalCode: string, city: string, country: string) => {
  return `${address}, ${postalCode} ${city} ${country}`;
};

export const availableAccountBalance = (
  spvPayout: Payout[] | null | undefined,
  accountBalance: number | string,
  string: boolean,
): number | string => {
  if (typeof accountBalance === 'string') accountBalance = Number(accountBalance);
  if (!accountBalance) accountBalance = 0;
  if (!spvPayout) return string ? accountBalance.toFixed(2) : accountBalance;
  const frozenAmount = spvPayout.reduce((acc: number, payout: any) => acc + Number(payout.payoutAmount.$numberDecimal), 0);
  const availableAccountBalance = accountBalance - frozenAmount;
  if (string) {
    return availableAccountBalance.toFixed(2);
  }
  return availableAccountBalance;
};

export const returnCurrentLangTranslationCode = (language: string) => {
  const table = {
    en: 'eng',
    de: 'deu',
  };
  return table[language as keyof typeof table];
};

export const calcEmittedBricks = (asset: Asset) => {
  return (asset?.investmentDetails?.totalBricks ?? 0) - (asset.investmentDetails.availableBricks ?? 0);
};

export const downloadXML = (xml: string, fileName: string) => {
  const element = document.createElement('a');
  element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(xml));
  element.setAttribute('download', fileName);

  element.style.display = 'none';
  document.body.appendChild(element);

  element.click();

  document.body.removeChild(element);
};

/**
 * This function is used to extract all values of an object of any depth to a an array.
 * The function first iterates over the object's entries, and if the value is an object, it recursively calls itself, otherwise it returns the value.
 * @params obj: object - The object to be converted to a flat array.
 * @returns T[] - The flat array of values.
 */
export const flattenObjectToArray = <T>(obj: object): T[] => {
  let result: T[] = [];

  for (const value of Object.values(obj)) {
    if (typeof value === 'object') {
      result = result.concat(flattenObjectToArray<T>(value));
    } else {
      result.push(value as T);
    }
  }

  return result;
};

/**
 * Recursively removes null and undefined values from an object without mutating it.
 * @param obj object to remove null and undefined values from
 * @returns object without null and undefined values
 */
export const removeNullUndefined = <T>(obj: T): T => {
  if (obj === null || obj === undefined || NaN) {
    return obj;
  }

  if (typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(removeNullUndefined) as T;
  }

  // To avoid converting Date objects to empty objects
  if (Object.entries(obj).length === 0) return obj;

  return Object.fromEntries(
    Object.entries(obj)
      .filter(([, value]) => value !== null && value !== undefined && value !== '' && !Number.isNaN(value))
      .map(([key, value]): any => [key, removeNullUndefined(value)]),
  ) as T;
};

export const payoutFilterPendingInitQueued = (payouts: Payout[]) =>
  payouts.filter((p) => p.status === PayoutStatus.pending || p.status === PayoutStatus.init || p.status === PayoutStatus.queued);

type Relaxed<T> = {
  [K in keyof T]: T[K] | undefined;
};
export function sortCSVHeader<T extends object>(objectToSort: T[], sortOrder: Array<keyof T>) {
  const sortedArray: T[] = objectToSort.map((item: T) => {
    const sortedItem: Partial<Relaxed<T>> = {};
    sortOrder.forEach((key: keyof T) => {
      if (item.hasOwnProperty(key)) {
        sortedItem[key] = item[key] as T[typeof key];
      }
    });
    return sortedItem as T;
  });
  return sortedArray;
}

export const randomFromEnum = (inputEnum: any) => {
  return faker.helpers.arrayElement(Object.values(inputEnum));
};
